import { useCallback, useEffect, useState } from "react";
import { ArrowsLeftRight, FilePdf, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, formatCurrency, fmtErr } from "../lib/api";
import { InvoiceForm } from "../components/InvoiceForm";
import { InvoiceDetail } from "../components/InvoiceDetail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const CONFIG = {
  estimate: {
    endpoint: "/estimates",
    convertTo: "estimates",
    title: "Estimates",
    subtitle: "Quotations you can convert to tax invoices in one click",
    convertLabel: "Convert to Invoice",
    convertTitle: "tax invoice",
    empty: "No estimates yet",
    emptyHint: "Send a quotation first — convert it to a GST invoice when the customer confirms.",
    testId: "estimates-page",
    tableTestId: "estimates-table",
  },
  po: {
    endpoint: "/purchase-orders",
    convertTo: "purchase-orders",
    title: "Purchase Orders",
    subtitle: "Orders to vendors you can convert to purchase bills",
    convertLabel: "Convert to Bill",
    convertTitle: "purchase bill",
    empty: "No purchase orders yet",
    emptyHint: "Raise a PO to your vendor — convert it to a purchase bill when goods arrive.",
    testId: "purchase-orders-page",
    tableTestId: "purchase-orders-table",
  },
};

export function DocumentList({ type }) {
  const cfg = CONFIG[type];
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [converting, setConverting] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await API.get(cfg.endpoint);
      setDocs(r.data);
      setSelected((sel) => (sel ? r.data.find((d) => d.id === sel.id) || null : null));
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  }, [cfg.endpoint]);

  useEffect(() => {
    load();
  }, [load]);

  const convert = async (d) => {
    if (!window.confirm(`Convert ${d.number} to a ${cfg.convertTitle}? Stock will update automatically.`)) return;
    setConverting(d.id);
    try {
      const r = await API.post(`/${cfg.convertTo}/${d.id}/convert`);
      toast.success(`${d.number} converted to ${r.data.number}`);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setConverting("");
    }
  };

  return (
    <div className="space-y-6" data-testid={cfg.testId}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{cfg.title}</h1>
          <p className="text-sm text-muted-foreground">{cfg.subtitle}</p>
        </div>
        <InvoiceForm type={type} onSaved={load} />
      </div>

      <div className="rounded-lg border bg-card">
        {loading ? (
          <p className="p-10 text-center text-sm text-muted-foreground">Loading…</p>
        ) : docs.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-state">
            <p className="font-heading text-lg font-semibold">{cfg.empty}</p>
            <p className="mt-1 text-sm text-muted-foreground">{cfg.emptyHint}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid={cfg.tableTestId} className="min-w-[640px]">
              <TableHeader>
                <TableRow>
                  <TableHead>No.</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>{type === "estimate" ? "Customer" : "Vendor"}</TableHead>
                  <TableHead className="text-right">Taxable</TableHead>
                  <TableHead className="text-right">GST</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map((d) => (
                  <TableRow key={d.id} className="transition-colors hover:bg-muted/50" data-testid={`doc-row-${d.number}`}>
                    <TableCell className="cursor-pointer font-medium" onClick={() => setSelected(d)}>{d.number}</TableCell>
                    <TableCell>{d.date}</TableCell>
                    <TableCell>{d.party_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(d.taxable / (Number(d.exchange_rate) || 1), d.currency || "INR")}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency((d.cgst + d.sgst + d.igst) / (Number(d.exchange_rate) || 1), d.currency || "INR")}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{formatCurrency(d.total / (Number(d.exchange_rate) || 1), d.currency || "INR")}</TableCell>
                    <TableCell>
                      {d.status === "converted" ? (
                        <Badge variant="outline" className="border-[#008A52]/30 bg-[#008A52]/10 text-[#008A52]" data-testid={`status-${d.number}`}>
                          → {d.converted_number}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-[#B7791F]/30 bg-[#B7791F]/10 text-[#B7791F]" data-testid={`status-${d.number}`}>Open</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {d.status !== "converted" && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={converting === d.id}
                            onClick={() => convert(d)}
                            data-testid={`convert-${d.number}`}
                          >
                            <ArrowsLeftRight size={14} className="mr-1" />
                            {converting === d.id ? "Converting…" : cfg.convertLabel}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setSelected(d)} data-testid={`pdf-${d.number}`}>
                          <FilePdf size={15} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <InvoiceDetail doc={selected} onClose={() => setSelected(null)} onChanged={load} />
    </div>
  );
}

export default function Estimates() {
  return <DocumentList type="estimate" />;
}
